import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useOutletContext } from 'react-router-dom';
import { 
  ArrowLeft,
  Save,
  Trash2,
  Mail,
  Phone,
  MapPin,
  Globe,
  Calendar,
  User,
  UserCheck,
  Link2,
  Smartphone,
  Plus,
  X,
  AlertCircle,
  Loader,
  Clock,
  Send,
  Edit2,
  Check,
  RefreshCw
} from 'lucide-react';
import { supabase } from '../../utils/supabaseClient';
import '../../styles/CuratorContactDetail.css';

/**
 * CuratorContactDetail
 * 
 * Full page for viewing and editing a single external organization member.
 * Shows:
 * - Contact information with primary/secondary emails and phones
 * - VibrantCast user link status
 * - App usage status (if they have the app installed)
 * - Membership details
 * - Notes
 */

const CuratorContactDetail = () => {
  const { contactId } = useParams();
  const navigate = useNavigate();
  const { selectedOrganization } = useOutletContext() || {};
  
  const isNewContact = contactId === 'new';
  
  // Data state
  const [contact, setContact] = useState(null);
  const [loading, setLoading] = useState(!isNewContact);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  
  // App usage info
  const [appInfo, setAppInfo] = useState(null);
  
  // Delete confirmation
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    additional_emails: [''],
    phone_primary: '',
    additional_phones: [''],
    street: '',
    city: '',
    state: '',
    zip: '',
    website: '',
    membership_status: 'active',
    join_date: '',
    renewal_date: '',
    auto_renew: false,
    notes: ''
  });
  const [formErrors, setFormErrors] = useState({});
  const [hasChanges, setHasChanges] = useState(false);

  // Format date helper
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  // Load contact data
  const loadContact = async () => {
    if (isNewContact) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase
        .from('external_organization_members')
        .select(`
          *,
          linked_user:user_uuid (
            uuid,
            first_name,
            last_name,
            email,
            profile_img
          )
        `)
        .eq('id', contactId)
        .single();

      if (fetchError) throw fetchError;
      if (!data) throw new Error('Contact not found');

      setContact(data);
      
      // Populate form
      setFormData({
        first_name: data.first_name || '',
        last_name: data.last_name || '',
        email: data.email || '',
        additional_emails: data.additional_emails?.length ? data.additional_emails : [''],
        phone_primary: data.phone_primary || '',
        additional_phones: data.additional_phones?.length ? data.additional_phones : [''],
        street: data.street || '',
        city: data.city || '',
        state: data.state || '',
        zip: data.zip || '',
        website: data.website || '',
        membership_status: data.membership_status || 'active',
        join_date: data.join_date || '',
        renewal_date: data.renewal_date || '',
        auto_renew: data.auto_renew || false,
        notes: data.notes || ''
      });

      // If linked to a user, check app usage
      if (data.user_uuid) {
        await loadAppInfo(data.user_uuid);
      }

    } catch (err) {
      console.error('Error loading contact:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Load app usage info
  const loadAppInfo = async (userUuid) => {
    try {
      const { data, error } = await supabase
        .from('user_player_ids')
        .select('updated_at, device_info, created_at')
        .eq('user_id', userUuid)
        .order('updated_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (!error && data) {
        setAppInfo({
          hasApp: true,
          lastActive: data.updated_at,
          deviceType: data.device_info?.device_type || 'unknown',
          createdAt: data.created_at
        });
      } else {
        setAppInfo({ hasApp: false });
      }
    } catch (err) {
      console.error('Error loading app info:', err);
      setAppInfo({ hasApp: false });
    }
  };

  useEffect(() => {
    loadContact();
  }, [contactId]);

  // Track form changes
  useEffect(() => {
    if (!contact && !isNewContact) return;
    
    const originalData = contact || {};
    const changed = 
      formData.first_name !== (originalData.first_name || '') ||
      formData.last_name !== (originalData.last_name || '') ||
      formData.email !== (originalData.email || '') ||
      formData.phone_primary !== (originalData.phone_primary || '') ||
      formData.street !== (originalData.street || '') ||
      formData.city !== (originalData.city || '') ||
      formData.state !== (originalData.state || '') ||
      formData.zip !== (originalData.zip || '') ||
      formData.website !== (originalData.website || '') ||
      formData.membership_status !== (originalData.membership_status || 'active') ||
      formData.join_date !== (originalData.join_date || '') ||
      formData.renewal_date !== (originalData.renewal_date || '') ||
      formData.auto_renew !== (originalData.auto_renew || false) ||
      formData.notes !== (originalData.notes || '');
    
    setHasChanges(changed || isNewContact);
  }, [formData, contact, isNewContact]);

  // Form validation
  const validateForm = () => {
    const errors = {};
    
    if (!formData.first_name?.trim()) {
      errors.first_name = 'First name is required';
    }
    if (!formData.last_name?.trim()) {
      errors.last_name = 'Last name is required';
    }
    if (!formData.email?.trim()) {
      errors.email = 'Primary email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Invalid email format';
    }
    
    // Validate additional emails
    formData.additional_emails.forEach((email, idx) => {
      if (email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        errors[`additional_email_${idx}`] = 'Invalid email format';
      }
    });
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle save
  const handleSave = async () => {
    if (!validateForm()) return;

    try {
      setSaving(true);
      setError(null);

      const cleanedEmails = formData.additional_emails.filter(e => e.trim());
      const cleanedPhones = formData.additional_phones.filter(p => p.trim());

      const payload = {
        first_name: formData.first_name.trim(),
        last_name: formData.last_name.trim(),
        email: formData.email.trim().toLowerCase(),
        additional_emails: cleanedEmails.length ? cleanedEmails : null,
        phone_primary: formData.phone_primary?.trim() || null,
        additional_phones: cleanedPhones.length ? cleanedPhones : null,
        street: formData.street?.trim() || null,
        city: formData.city?.trim() || null,
        state: formData.state?.trim() || null,
        zip: formData.zip?.trim() || null,
        website: formData.website?.trim() || null,
        membership_status: formData.membership_status,
        join_date: formData.join_date || null,
        renewal_date: formData.renewal_date || null,
        auto_renew: formData.auto_renew,
        notes: formData.notes?.trim() || null
      };

      if (isNewContact) {
        payload.organization_id = selectedOrganization.organization_id;
        payload.import_source = 'manual';
        
        const { data, error: insertError } = await supabase
          .from('external_organization_members')
          .insert(payload)
          .select()
          .single();

        if (insertError) throw insertError;
        
        setSuccessMessage('Contact created successfully');
        setTimeout(() => {
          navigate(`/curator/contacts/${data.id}`);
        }, 1000);
      } else {
        const { data, error: updateError } = await supabase
          .from('external_organization_members')
          .update(payload)
          .eq('id', contactId)
          .select()
          .single();

        if (updateError) throw updateError;
        
        setContact(data);
        setSuccessMessage('Contact updated successfully');
        setHasChanges(false);
        setTimeout(() => setSuccessMessage(null), 3000);
      }

    } catch (err) {
      console.error('Error saving contact:', err);
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  // Handle delete
  const handleDelete = async () => {
    try {
      setSaving(true);
      
      const { error: deleteError } = await supabase
        .from('external_organization_members')
        .delete()
        .eq('id', contactId);

      if (deleteError) throw deleteError;
      
      navigate('/curator/contacts');
      
    } catch (err) {
      console.error('Error deleting contact:', err);
      setError(err.message);
      setSaving(false);
    }
  };

  // Add additional email field
  const addEmailField = () => {
    setFormData(prev => ({
      ...prev,
      additional_emails: [...prev.additional_emails, '']
    }));
  };

  // Remove additional email field
  const removeEmailField = (index) => {
    setFormData(prev => ({
      ...prev,
      additional_emails: prev.additional_emails.filter((_, i) => i !== index)
    }));
  };

  // Update additional email
  const updateAdditionalEmail = (index, value) => {
    setFormData(prev => ({
      ...prev,
      additional_emails: prev.additional_emails.map((e, i) => i === index ? value : e)
    }));
  };

  // Add additional phone field
  const addPhoneField = () => {
    setFormData(prev => ({
      ...prev,
      additional_phones: [...prev.additional_phones, '']
    }));
  };

  // Remove additional phone field
  const removePhoneField = (index) => {
    setFormData(prev => ({
      ...prev,
      additional_phones: prev.additional_phones.filter((_, i) => i !== index)
    }));
  };

  // Update additional phone
  const updateAdditionalPhone = (index, value) => {
    setFormData(prev => ({
      ...prev,
      additional_phones: prev.additional_phones.map((p, i) => i === index ? value : p)
    }));
  };

  // Get initials for avatar
  const getInitials = () => {
    const first = formData.first_name?.[0] || '';
    const last = formData.last_name?.[0] || '';
    return (first + last).toUpperCase() || '?';
  };

  if (loading) {
    return (
      <div className="curator-contact-detail">
        <div className="loading-state">
          <Loader size={32} className="spin" />
          <p>Loading contact...</p>
        </div>
      </div>
    );
  }

  if (error && !contact && !isNewContact) {
    return (
      <div className="curator-contact-detail">
        <div className="error-state">
          <AlertCircle size={48} />
          <h3>Error Loading Contact</h3>
          <p>{error}</p>
          <button className="btn-secondary" onClick={() => navigate('/curator/contacts')}>
            <ArrowLeft size={16} />
            Back to Contacts
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="curator-contact-detail">
      {/* Header */}
      <div className="contact-detail-header">
        <button className="back-button" onClick={() => navigate('/curator/contacts')}>
          <ArrowLeft size={20} />
          <span>Contacts</span>
        </button>
        
        <div className="header-actions">
          {!isNewContact && (
            <button 
              className="btn-icon"
              onClick={() => navigate(`/curator/contacts/email?ids=${contactId}`)}
              title="Send Email"
            >
              <Mail size={18} />
            </button>
          )}
          
          {!isNewContact && (
            <button 
              className="btn-icon danger"
              onClick={() => setShowDeleteConfirm(true)}
              title="Delete Contact"
            >
              <Trash2 size={18} />
            </button>
          )}
          
          <button 
            className="btn-primary"
            onClick={handleSave}
            disabled={saving || (!hasChanges && !isNewContact)}
          >
            {saving ? (
              <>
                <Loader size={16} className="spin" />
                Saving...
              </>
            ) : (
              <>
                <Save size={16} />
                {isNewContact ? 'Create Contact' : 'Save Changes'}
              </>
            )}
          </button>
        </div>
      </div>

      {/* Success/Error Messages */}
      {successMessage && (
        <div className="success-banner">
          <Check size={16} />
          {successMessage}
        </div>
      )}
      
      {error && (
        <div className="error-banner">
          <AlertCircle size={16} />
          {error}
          <button onClick={() => setError(null)}>
            <X size={14} />
          </button>
        </div>
      )}

      <div className="contact-detail-content">
        {/* Left Column - Main Info */}
        <div className="contact-main-column">
          {/* Profile Header Card */}
          <div className="profile-header-card">
            <div className="profile-avatar-section">
              {contact?.linked_user?.profile_img ? (
                <img 
                  src={contact.linked_user.profile_img} 
                  alt={`${formData.first_name} ${formData.last_name}`}
                  className="profile-avatar-large"
                />
              ) : (
                <div className="profile-avatar-large-placeholder">
                  {getInitials()}
                </div>
              )}
              
              {contact?.user_uuid && (
                <div className="profile-linked-badge">
                  <Link2 size={14} />
                  <span>VibrantCast User</span>
                </div>
              )}
            </div>
            
            <div className="profile-name-section">
              <h1 className="profile-name">
                {formData.first_name || formData.last_name 
                  ? `${formData.first_name} ${formData.last_name}`.trim()
                  : 'New Contact'}
              </h1>
              {contact?.membership_status && (
                <span className={`status-badge status-${contact.membership_status}`}>
                  {contact.membership_status}
                </span>
              )}
            </div>
          </div>

          {/* Basic Information */}
          <div className="detail-card">
            <div className="card-header">
              <User size={18} />
              <h2>Basic Information</h2>
            </div>
            <div className="card-content">
              <div className="form-row">
                <div className="form-group">
                  <label>First Name <span className="required">*</span></label>
                  <input
                    type="text"
                    value={formData.first_name}
                    onChange={(e) => setFormData(prev => ({ ...prev, first_name: e.target.value }))}
                    className={formErrors.first_name ? 'input-error' : ''}
                    placeholder="Enter first name"
                  />
                  {formErrors.first_name && <span className="field-error">{formErrors.first_name}</span>}
                </div>
                <div className="form-group">
                  <label>Last Name <span className="required">*</span></label>
                  <input
                    type="text"
                    value={formData.last_name}
                    onChange={(e) => setFormData(prev => ({ ...prev, last_name: e.target.value }))}
                    className={formErrors.last_name ? 'input-error' : ''}
                    placeholder="Enter last name"
                  />
                  {formErrors.last_name && <span className="field-error">{formErrors.last_name}</span>}
                </div>
              </div>
            </div>
          </div>

          {/* Contact Details */}
          <div className="detail-card">
            <div className="card-header">
              <Mail size={18} />
              <h2>Contact Details</h2>
            </div>
            <div className="card-content">
              {/* Primary Email */}
              <div className="form-group">
                <label>Primary Email <span className="required">*</span></label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  className={formErrors.email ? 'input-error' : ''}
                  placeholder="primary@example.com"
                />
                {formErrors.email && <span className="field-error">{formErrors.email}</span>}
              </div>

              {/* Additional Emails */}
              <div className="form-group">
                <label>Additional Emails</label>
                {formData.additional_emails.map((email, index) => (
                  <div key={index} className="multi-input-row">
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => updateAdditionalEmail(index, e.target.value)}
                      className={formErrors[`additional_email_${index}`] ? 'input-error' : ''}
                      placeholder="additional@example.com"
                    />
                    <button 
                      type="button"
                      className="remove-field-btn"
                      onClick={() => removeEmailField(index)}
                      title="Remove email"
                    >
                      <X size={16} />
                    </button>
                  </div>
                ))}
                <button type="button" className="add-field-btn" onClick={addEmailField}>
                  <Plus size={14} />
                  Add Email
                </button>
              </div>

              <div className="form-divider" />

              {/* Primary Phone */}
              <div className="form-group">
                <label>Primary Phone (Mobile)</label>
                <input
                  type="tel"
                  value={formData.phone_primary}
                  onChange={(e) => setFormData(prev => ({ ...prev, phone_primary: e.target.value }))}
                  placeholder="(555) 123-4567"
                />
              </div>

              {/* Additional Phones */}
              <div className="form-group">
                <label>Additional Phones</label>
                {formData.additional_phones.map((phone, index) => (
                  <div key={index} className="multi-input-row">
                    <input
                      type="tel"
                      value={phone}
                      onChange={(e) => updateAdditionalPhone(index, e.target.value)}
                      placeholder="(555) 987-6543"
                    />
                    <button 
                      type="button"
                      className="remove-field-btn"
                      onClick={() => removePhoneField(index)}
                      title="Remove phone"
                    >
                      <X size={16} />
                    </button>
                  </div>
                ))}
                <button type="button" className="add-field-btn" onClick={addPhoneField}>
                  <Plus size={14} />
                  Add Phone
                </button>
              </div>

              <div className="form-divider" />

              {/* Website */}
              <div className="form-group">
                <label>Website</label>
                <input
                  type="url"
                  value={formData.website}
                  onChange={(e) => setFormData(prev => ({ ...prev, website: e.target.value }))}
                  placeholder="https://example.com"
                />
              </div>
            </div>
          </div>

          {/* Address */}
          <div className="detail-card">
            <div className="card-header">
              <MapPin size={18} />
              <h2>Address</h2>
            </div>
            <div className="card-content">
              <div className="form-group">
                <label>Street Address</label>
                <input
                  type="text"
                  value={formData.street}
                  onChange={(e) => setFormData(prev => ({ ...prev, street: e.target.value }))}
                  placeholder="123 Main Street"
                />
              </div>
              <div className="form-row three-cols">
                <div className="form-group">
                  <label>City</label>
                  <input
                    type="text"
                    value={formData.city}
                    onChange={(e) => setFormData(prev => ({ ...prev, city: e.target.value }))}
                    placeholder="Chicago"
                  />
                </div>
                <div className="form-group">
                  <label>State</label>
                  <input
                    type="text"
                    value={formData.state}
                    onChange={(e) => setFormData(prev => ({ ...prev, state: e.target.value }))}
                    placeholder="IL"
                    maxLength={2}
                  />
                </div>
                <div className="form-group">
                  <label>ZIP Code</label>
                  <input
                    type="text"
                    value={formData.zip}
                    onChange={(e) => setFormData(prev => ({ ...prev, zip: e.target.value }))}
                    placeholder="60601"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Notes */}
          <div className="detail-card">
            <div className="card-header">
              <Edit2 size={18} />
              <h2>Notes</h2>
            </div>
            <div className="card-content">
              <div className="form-group">
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Add any notes about this contact..."
                  rows={4}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Right Column - Status & App Info */}
        <div className="contact-side-column">
          {/* Membership Status */}
          <div className="detail-card">
            <div className="card-header">
              <Calendar size={18} />
              <h2>Membership</h2>
            </div>
            <div className="card-content">
              <div className="form-group">
                <label>Status</label>
                <select
                  value={formData.membership_status}
                  onChange={(e) => setFormData(prev => ({ ...prev, membership_status: e.target.value }))}
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="pending">Pending</option>
                  <option value="expired">Expired</option>
                  <option value="suspended">Suspended</option>
                </select>
              </div>
              <div className="form-group">
                <label>Join Date</label>
                <input
                  type="date"
                  value={formData.join_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, join_date: e.target.value }))}
                />
              </div>
              <div className="form-group">
                <label>Renewal Date</label>
                <input
                  type="date"
                  value={formData.renewal_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, renewal_date: e.target.value }))}
                />
              </div>
              <div className="form-group checkbox-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.auto_renew}
                    onChange={(e) => setFormData(prev => ({ ...prev, auto_renew: e.target.checked }))}
                  />
                  Auto-renew membership
                </label>
              </div>
            </div>
          </div>

          {/* VibrantCast Status */}
          {!isNewContact && (
            <div className="detail-card">
              <div className="card-header">
                <Smartphone size={18} />
                <h2>VibrantCast Status</h2>
              </div>
              <div className="card-content">
                {contact?.user_uuid ? (
                  <>
                    <div className="status-item success">
                      <UserCheck size={20} />
                      <div className="status-info">
                        <span className="status-label">Linked User</span>
                        <span className="status-value">
                          {contact.linked_user?.first_name} {contact.linked_user?.last_name}
                        </span>
                      </div>
                    </div>
                    
                    {appInfo?.hasApp ? (
                      <>
                        <div className="status-item success">
                          <Smartphone size={20} />
                          <div className="status-info">
                            <span className="status-label">Has App Installed</span>
                            <span className="status-value capitalize">{appInfo.deviceType}</span>
                          </div>
                        </div>
                        <div className="status-item">
                          <Clock size={20} />
                          <div className="status-info">
                            <span className="status-label">Last Active</span>
                            <span className="status-value">{formatDateTime(appInfo.lastActive)}</span>
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="status-item muted">
                        <Smartphone size={20} />
                        <div className="status-info">
                          <span className="status-label">App Status</span>
                          <span className="status-value">Not installed</span>
                        </div>
                      </div>
                    )}
                    
                    {contact.converted_at && (
                      <div className="status-item">
                        <Calendar size={20} />
                        <div className="status-info">
                          <span className="status-label">Converted</span>
                          <span className="status-value">{formatDate(contact.converted_at)}</span>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <div className="status-item muted">
                      <User size={20} />
                      <div className="status-info">
                        <span className="status-label">VibrantCast Account</span>
                        <span className="status-value">Not linked</span>
                      </div>
                    </div>
                    {contact?.invitation_sent_at && (
                      <div className="status-item">
                        <Send size={20} />
                        <div className="status-info">
                          <span className="status-label">Invitation Sent</span>
                          <span className="status-value">{formatDateTime(contact.invitation_sent_at)}</span>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          )}

          {/* Quick Actions */}
          {!isNewContact && (
            <div className="detail-card">
              <div className="card-header">
                <Send size={18} />
                <h2>Quick Actions</h2>
              </div>
              <div className="card-content">
                <button 
                  className="action-button"
                  onClick={() => navigate(`/curator/contacts/email?ids=${contactId}`)}
                >
                  <Mail size={16} />
                  Send Email
                </button>
                
                {formData.phone_primary && (
                  <a 
                    href={`tel:${formData.phone_primary}`}
                    className="action-button"
                  >
                    <Phone size={16} />
                    Call {formData.phone_primary}
                  </a>
                )}
                
                {formData.website && (
                  <a 
                    href={formData.website.startsWith('http') ? formData.website : `https://${formData.website}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="action-button"
                  >
                    <Globe size={16} />
                    Visit Website
                  </a>
                )}
              </div>
            </div>
          )}

          {/* Metadata */}
          {!isNewContact && contact && (
            <div className="detail-card metadata-card">
              <div className="metadata-item">
                <span className="metadata-label">Created</span>
                <span className="metadata-value">{formatDateTime(contact.created_at)}</span>
              </div>
              <div className="metadata-item">
                <span className="metadata-label">Last Updated</span>
                <span className="metadata-value">{formatDateTime(contact.updated_at)}</span>
              </div>
              {contact.import_source && (
                <div className="metadata-item">
                  <span className="metadata-label">Import Source</span>
                  <span className="metadata-value">{contact.import_source}</span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="modal-overlay" onClick={() => setShowDeleteConfirm(false)}>
          <div className="modal-content delete-confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="delete-confirm-icon">
              <AlertCircle size={48} />
            </div>
            <h3>Delete Contact?</h3>
            <p>
              Are you sure you want to delete <strong>{formData.first_name} {formData.last_name}</strong>?
              This action cannot be undone.
            </p>
            <div className="delete-confirm-actions">
              <button className="btn-secondary" onClick={() => setShowDeleteConfirm(false)}>
                Cancel
              </button>
              <button className="btn-danger" onClick={handleDelete} disabled={saving}>
                {saving ? <Loader size={16} className="spin" /> : <Trash2 size={16} />}
                Delete Contact
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CuratorContactDetail;
